import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../../store';
import { getMedicationSchedule } from '../../../../../../store/hubOwner/actionCreators';
import { DateTime } from 'luxon';
import { MedicationEvent } from '../types';

export const useMedicationEvents = (
  ownerId: number,
  weekStartDate: moment.Moment,
  weekEndDate: moment.Moment,
  ownerName?: string,
  includeInvalidated: boolean = true,
): { events: MedicationEvent[]; isLoading: boolean } => {
  const dispatch = useDispatch();
  const { medSchedule, schedule_local_timezone } = useSelector(
    (state: RootState) => state.hubOwner,
  );
  const { isLoading } = useSelector((state: RootState) => state.customers);

  useEffect(() => {
    if (!ownerId) return;
    const startDateLuxon = DateTime.fromJSDate(weekStartDate.toDate());
    const endDateLuxon = DateTime.fromJSDate(weekEndDate.toDate());
    dispatch(
      getMedicationSchedule(
        ownerId,
        startDateLuxon,
        endDateLuxon,
        includeInvalidated,
      ),
    );
  }, [dispatch, ownerId, weekStartDate, weekEndDate, includeInvalidated]);

  // Map API response to MedicationEvent UI structure
  function mapReduxEventToMedicationEvent(apiEvent: any): MedicationEvent {
    const rawStart = apiEvent.desired_datetime || apiEvent.prescribed_datetime;

    const start = rawStart ? new Date(rawStart) : new Date();
    const end = start ? new Date(start.getTime() + 30 * 60000) : new Date();

    const partOfDayLabels: Record<string, string> = {
      Morning: 'Morning meds',
      Afternoon: 'Lunch meds',
      Evening: 'Evening meds',
    };
    return {
      id: apiEvent.med_event_id,
      title: partOfDayLabels[apiEvent.part_of_day] || 'Medication Event',
      start,
      end,
      type: apiEvent.event_type || 'pending',
      status: apiEvent.status || 'pending',
      medications:
        apiEvent.doses?.map((dose: any) => ({
          id: dose.dose_id?.toString() || '',
          name: dose.drug_name || '',
          dosage: dose.tablet_count ? `${dose.tablet_count} units` : '',
          quantity: dose.tablet_count || 1,
          status: dose.status || 'pending',
        })) || [],
      caregiver:
        apiEvent.caregiver_name && apiEvent.caregiver_name.trim()
          ? apiEvent.caregiver_name
          : 'N/A',
      owner: ownerName || 'N/A',
      timezone: schedule_local_timezone || '',
      caregiver_customer_id: apiEvent.caregiver_customer_id || '',
      prescribed_datetime: apiEvent.prescribed_datetime || '',
      desired_datetime: apiEvent.desired_datetime || '',
      has_dispense_images: apiEvent.has_dispense_images === true,
    };
  }

  const events = Array.isArray(medSchedule)
    ? medSchedule.map(mapReduxEventToMedicationEvent)
    : [];

  return { events, isLoading };
};
